import { callApi } from '@/api/api.config';
import {
  FieldNutrientInfo,
  FieldSocNutrientApiResponse,
  FieldSummaryType,
  MultiBarScatterChartData,
  NutrientInfoApiResponse,
  ProjectBasicDetailsApiResponse,
  ProjectBasicDetailsData,
  ProjectFarmSummaryApiResponse,
  ProjectFarmSummaryType,
  ReportGenerateData,
  ReportStatusData,
} from '@/modules/Report/Report.schema';
import { useMutation, useQuery } from '@tanstack/react-query';

const fetchSoilPyramidData = async (fieldId: string) => {
  return callApi({ method: 'GET', url: `carbon/soil_textures/${fieldId}/` }).then((response) => response.data);
};

export const useSoilPyramidData = (fieldId: string) => {
  return useQuery({
    queryKey: ['soilPyramid', fieldId],
    queryFn: () => fetchSoilPyramidData(fieldId),
  });
};

const fetchSoilPhData = async (fieldId: string) => {
  return callApi({ method: 'GET', url: `carbon/soilPh/${fieldId}/` }).then((response) => response.data);
};

export const useSoilPhData = (fieldId: string) => {
  return useQuery({
    queryKey: ['soilPh', fieldId],
    queryFn: () => fetchSoilPhData(fieldId),
  });
};

const fetchOrganicMatterData = async (fieldId: string) => {
  return callApi({ method: 'GET', url: `carbon/organic_matter/${fieldId}/` }).then((response) => response.data);
};

export const useOrganicMatterData = (fieldId: string) => {
  return useQuery({
    queryKey: ['organicMatterData', fieldId],
    queryFn: () => fetchOrganicMatterData(fieldId),
  });
};

const fetchFarmData = async (farmId: string) => {
  return callApi({ method: 'GET', url: `farm/summary/farm/${farmId}/` }).then((response) => response.data);
};

export const useFarmData = (farmId?: string) => {
  return useQuery({
    queryKey: ['farmData', farmId],
    queryFn: () => farmId && fetchFarmData(farmId),
    enabled: !!farmId,
  });
};

const fetchProjectData = async (projectId: string) => {
  return callApi({ method: 'GET', url: `customer/projects/${projectId}/` }).then((response) => response.data);
};

export const useProjectData = (projectId?: string) => {
  return useQuery({
    queryKey: ['projectData', projectId],
    queryFn: () => projectId && fetchProjectData(projectId),
    enabled: !!projectId,
  });
};

const fetchFieldData = async (fieldId: string) => {
  return callApi({ method: 'GET', url: `farm/summary/field/${fieldId}/` }).then((response) => response.data);
};

export const useFieldData = (fieldId?: string) => {
  return useQuery({
    queryKey: ['fieldData', fieldId],
    queryFn: () => fieldId && fetchFieldData(fieldId),
    enabled: !!fieldId,
  });
};

const fetchFieldCarbonStockData = async (fieldId: string) => {
  return callApi({ method: 'GET', url: `carbon/field_carbon_stock/${fieldId}/` }).then((response) => response.data);
};

export const useFieldCarbonStockData = (fieldId?: string) => {
  return useQuery({
    queryKey: ['fieldCarbonStockData', fieldId],
    queryFn: () => fieldId && fetchFieldCarbonStockData(fieldId),
    enabled: !!fieldId,
  });
};

const fetchFieldSummaryData = async (farmId: string) => {
  return callApi({ method: 'GET', url: `/carbon/field_soc_nutrients/${farmId}/` }).then((response) => response.data);
};

const mapFieldSummaryData = (apiData: FieldSocNutrientApiResponse[]): FieldSummaryType[] => {
  return apiData.map((field) => ({
    fieldId: field.field_id,
    fieldNumber: field.field_num,
    socPercentage: field.soc_pct,
    socTPerHa: field.carbon_stock_tpha,
    somPercentage: field.som_pct,
    country: field.country,
    pH: field.pH,
    k: field.K,
    kMpl: field.K_mg_per_l,
    mg: field.Mg,
    mgMpl: field.Mg_mg_per_l,
    pOlsen: field.P_Olsen,
    pMM: field.P_MM,
    li: field.Li,
    n: field.N,
    ca: field.Ca,
    caMpl: field.Ca_mg_per_l,
  }));
};

export const useFieldSummaryData = (farmId?: string) => {
  return useQuery({
    queryKey: ['fieldSummaryData', farmId],
    queryFn: () => farmId && fetchFieldSummaryData(farmId),
    select: (apiData) => mapFieldSummaryData(apiData),
    enabled: !!farmId,
  });
};

const mapFieldNutrientData = (apiData: NutrientInfoApiResponse[]): FieldNutrientInfo[] => {
  return apiData.map((item) => ({
    metric: item.metric,
    result: item.result,
    legend: item.legend,
    comment: item.comment,
    optimal: item.optimal_value,
  }));
};

const fetchFieldNutrientData = async (farmId: string, fieldId: string): Promise<FieldNutrientInfo[]> => {
  const data = await callApi({ method: 'GET', url: `/carbon/field_nutrients_info/${farmId}/?field_id=${fieldId}` }).then(
    (response) => response.data
  );
  return mapFieldNutrientData(data);
};

export const useFieldNutrientData = (farmId: string, fieldId: string) => {
  return useQuery({
    queryKey: ['fieldNutrientData', farmId, fieldId],
    queryFn: () => fetchFieldNutrientData(farmId, fieldId),
  });
};

const fetchDepthMetricsData = async (fieldId: string) => {
  return callApi({ method: 'GET', url: `carbon/depth_metrics/${fieldId}/` }).then((response) => response.data);
};

export const useDepthMetricsData = (fieldId: string) => {
  return useQuery({
    queryKey: ['DepthMetrics', fieldId],
    queryFn: () => fetchDepthMetricsData(fieldId),
  });
};

const fetchPeerGroupCarbonStockData = async (farmId: string, fieldId: string): Promise<MultiBarScatterChartData> => {
  return callApi({ method: 'GET', url: `/carbon/peer_group_carbon_stock/${farmId}/${fieldId}/` }).then((response) => response.data);
};

export const usePeerGroupCarbonStockData = (farmId: string, fieldId: string) => {
  return useQuery({
    queryKey: ['PeerGroupCarbonStock', farmId, fieldId],
    queryFn: () => fetchPeerGroupCarbonStockData(farmId, fieldId),
  });
};

const fetchProjectFarmSummaryData = async (projectId: string) => {
  return callApi({ method: 'GET', url: `/carbon/project_farm_summary/${projectId}/` }).then((response) => response.data);
};

const mapProjectFarmSummaryData = (apiData: ProjectFarmSummaryApiResponse[]): ProjectFarmSummaryType[] => {
  return apiData.map((farm) => ({
    farmId: farm.farm_id,
    farmName: farm.farm_name,
    farmAreaHa: farm.farm_area_ha,
    coreCount: farm.core_count,
    coresPerHectare: farm.cores_per_hectare,
    socPct: farm.soc_pct,
    totalCarbonStockTonnes: farm.total_carbon_stock_tonnes,
    avgCarbonStock: farm.avg_carbon_stock,
  }));
};

export const useProjectFarmSummaryData = (projectId: string) => {
  return useQuery({
    queryKey: ['projectFarmSummaryData', projectId],
    queryFn: () => fetchProjectFarmSummaryData(projectId),
    select: (apiData) => mapProjectFarmSummaryData(apiData),
    enabled: !!projectId,
  });
};

const fetchProjectBasicDetails = async (projectId: string) => {
  return callApi({ method: 'GET', url: `/carbon/project_basic_details/${projectId}/` }).then((response) => response.data);
};

const mapProjectBasicDetailsData = (apiData: ProjectBasicDetailsApiResponse): ProjectBasicDetailsData => ({
  projectId: apiData.project_id,
  projectName: apiData.project_name,
  csvDownloadable: apiData.csv_downloadable,
  coreExtractionDates: apiData.core_extraction_dates,
  sampleAnalysisDates: apiData.sample_analysis_dates,
  majorSoilType: apiData.major_soil_type,
  meanSoilDepthMm: apiData.mean_soil_depth_mm,
  totalProjectAreaHa: apiData.total_project_area_ha,
  totalNumberOfFields: apiData.total_number_of_fields,
  meanFieldAreaHa: apiData.mean_field_area_ha,
  totalCores: apiData.total_cores,
  coresPerHectare: apiData.cores_per_hectare,
  totalCarbonStockTonnes: apiData.total_carbon_stock_tonnes,
  avgCarbonStockTpha: apiData.avg_carbon_stock_tpha,
  stdDevCarbonStockTpha: apiData.std_dev_carbon_stock_tpha,
});

export const useProjectBasicDetailsData = (projectId: string) => {
  return useQuery({
    queryKey: ['ProjectBasicDetails', projectId],
    queryFn: () => fetchProjectBasicDetails(projectId),
    select: (apiData) => apiData && mapProjectBasicDetailsData(apiData),
  });
};

const fetchFarmReportStatus = async (farmId?: string): Promise<ReportStatusData> => {
  return callApi({ method: 'GET', url: `/carbon/farm_report/${farmId}/status/` }).then((response) => response.data);
};

export const useFarmReportStatus = (farmId?: string, refetchInterval?: number | false) => {
  const interval = refetchInterval || false;
  return useQuery({
    queryKey: ['reportStatus', farmId, interval],
    queryFn: () => fetchFarmReportStatus(farmId),
    refetchOnWindowFocus: false,
    refetchInterval: interval,
    enabled: !!farmId,
  });
};

const generateFarmReport = async (farmId: string): Promise<ReportGenerateData> => {
  return callApi({ method: 'POST', url: `/carbon/farm_report/${farmId}/generate/` }).then((response) => response.data);
};

export const useGenerateFarmReport = () => {
  return useMutation({
    mutationFn: (farmId: string) => generateFarmReport(farmId),
  });
};

const fetchProjectReportStatus = async (projectId?: string): Promise<ReportStatusData> => {
  return callApi({ method: 'GET', url: `/carbon/project_report/${projectId}/status/` }).then((response) => response.data);
};

export const useProjectReportStatus = (projectId?: string, refetchInterval?: number | false) => {
  const interval = refetchInterval || false;
  return useQuery({
    queryKey: ['projectReportStatus', projectId, interval],
    queryFn: () => fetchProjectReportStatus(projectId),
    refetchOnWindowFocus: false,
    refetchInterval: interval,
    enabled: !!projectId,
  });
};

const generateProjectReport = async (projectId: string): Promise<ReportGenerateData> => {
  return callApi({ method: 'POST', url: `/carbon/project_report/${projectId}/generate/` }).then((response) => response.data);
};

export const useGenerateProjectReport = () => {
  return useMutation({
    mutationFn: (projectId: string) => generateProjectReport(projectId),
  });
};
